/**
 * juliette-whisper — Moteur de priorité du "whisper" proactif de Juliette (CRM).
 *
 * Coeur de la Phase 2 (CRM analyste) du chantier "presence ambiante". A partir
 * des signaux en attente (juliette_signals) et de la conscience de page
 * (proactive ou non), determine si Juliette leve la main et avec quel message.
 *
 * Regle produit (brainstorm 08/06) : UNE seule chose a la fois. On ne sort que
 * LE signal le plus urgent + un compteur, jamais une liste qui noie l'agent.
 * Priorite = urgence commerciale : severite (urgent > warning > info) d'abord,
 * puis fraicheur (le plus recent devant) a severite egale.
 *
 * Pure et sans I/O : testable en isolation, reutilisee par la sidebar.
 */

import type { JulietteSignal } from "./juliette-signals";

export interface JulietteWhisper {
  /** Juliette leve la main : page proactive ET au moins un signal en attente. */
  active: boolean;
  /** Nombre de signaux en attente (exact, independamment de `active`). */
  count: number;
  /** LE signal le plus urgent (premier apres tri), ou null si aucun. */
  topSignal: JulietteSignal | null;
  /** Phrase courte affichee dans la sidebar (titre du signal le plus urgent). */
  headline: string;
}

/** Rang de tri par severite (urgent d'abord) — meme bareme que juliette-signals. */
const SEVERITY_RANK: Record<string, number> = { urgent: 0, warning: 1, info: 2 };

export function computeJulietteWhisper(
  signals: JulietteSignal[],
  proactive: boolean,
): JulietteWhisper {
  // Re-tri defensif : le "top" doit etre deterministe meme si la source ne trie
  // pas (urgence = severite puis fraicheur).
  const sorted = [...signals].sort(
    (a, b) =>
      (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9) ||
      b.detectedAt.localeCompare(a.detectedAt),
  );
  const topSignal = sorted[0] ?? null;
  const count = sorted.length;
  return {
    active: proactive && count > 0,
    count,
    topSignal,
    headline: topSignal?.title ?? "",
  };
}
